/**
 * Rechte — und warum Metarr nie mehr sieht als der Mensch.
 *
 * Der Beschluss lautet: „Metarr sieht nie mehr als der Benutzer, in dessen
 * Namen er handelt — strukturell erzwungen, nicht durch Sorgfalt im Prompt."
 *
 * Strukturell heißt hier konkret: Es gibt **keinen** Weg, eine `Sicht` zu
 * erzeugen, die keiner Person gehört. Jede Lese- und Schreibfunktion verlangt
 * eine `Sicht`, und Metarrs Sicht entsteht ausschließlich durch `fuerMetarr()`
 * aus der Sicht einer angemeldeten Person. Diese Funktion kann Rechte nur
 * **wegnehmen**, nie hinzufügen.
 *
 * ## Variante B — Rollen je Projekt und globale Fähigkeiten
 *
 * Zwei Ebenen, die zusammen die Sicht ergeben:
 *
 *   1. **Projektrollen** (`rollen`) — Sichtbarkeitsgrenze. Wer in
 *      `projekt_rollen` keine Zeile hat, sieht das Projekt nicht.
 *   2. **Globale Rechte** (`rechte`) — Fähigkeiten aus Gruppen
 *      (`system.admin`, `benutzer.verwalten`, …). Unabhängig vom Projekt.
 *
 * Schreiben in einem Projekt verlangt die passende Rolle. Verwaltungsvorgänge
 * außerhalb eines Projekts verlangen ein globales Recht.
 */
/* -------------------------------------------------------------------------
 * Rollen (pro Projekt)
 * ---------------------------------------------------------------------- */
/** Rollen innerhalb eines Projekts, aufsteigend. */
export const ROLLEN = ["gast", "mitglied", "betreuer", "verwalter"];
const RANG = {
    gast: 0,
    mitglied: 1,
    betreuer: 2,
    verwalter: 3,
};
export function mindestens(rolle, verlangt) {
    return RANG[rolle] >= RANG[verlangt];
}
/* -------------------------------------------------------------------------
 * Globale Rechte (aus Gruppen)
 * ---------------------------------------------------------------------- */
/**
 * Die Rechteschlüssel — deutsch benannt wie im Altsystem.
 *
 * Neue Schlüssel sind ein bewusster Eintrag hier, keine Migration.
 */
export const RECHTE = [
    // Administration
    "system.admin",
    "benutzer.verwalten",
    "gruppen.verwalten",
    "einstellungen.verwalten",
    "branding.verwalten",
    "freigaben.verwalten",
    "papierkorb.leeren",
    "export.nutzen",
    // Projekte & Aufgaben
    "projekte.verwalten",
    "aufgaben.bearbeiten",
    "gitlab.konfigurieren",
    "gitlab.code",
    "gitlab.review",
    "gitlab.merge",
    "gitlab.gate_uebersteuern",
    // Wissen & Content
    "seiten.bearbeiten",
    "content.bearbeiten",
    "content.veroeffentlichen",
    // Zusammenarbeit
    "kommentare.schreiben",
    "mcp.nutzen",
];
export const RECHTE_SET = new Set(RECHTE);
/** Beschreibungen für Oberfläche und Verwaltung. */
export const RECHTE_REGISTRY = [
    {
        schluessel: "system.admin",
        label: "Systemverwaltung",
        beschreibung: "Schließt alle anderen Rechte ein. Voller Zugriff auf die Instanz.",
        kategorie: "administration",
    },
    {
        schluessel: "benutzer.verwalten",
        label: "Benutzer verwalten",
        beschreibung: "Personen aktivieren, deaktivieren und Gruppen zuordnen.",
        kategorie: "administration",
    },
    {
        schluessel: "gruppen.verwalten",
        label: "Gruppen verwalten",
        beschreibung: "Gruppen anlegen, ändern und deren Rechte setzen.",
        kategorie: "administration",
    },
    {
        schluessel: "einstellungen.verwalten",
        label: "Einstellungen verwalten",
        beschreibung: "Instanzweite Einstellungen ändern.",
        kategorie: "administration",
    },
    {
        schluessel: "branding.verwalten",
        label: "Branding verwalten",
        beschreibung: "Erscheinungsbild der Instanz anpassen.",
        kategorie: "administration",
    },
    {
        schluessel: "freigaben.verwalten",
        label: "Freigaben verwalten",
        beschreibung: "Read-only-Freigabelinks für Außenstehende erzeugen und widerrufen.",
        kategorie: "administration",
    },
    {
        schluessel: "papierkorb.leeren",
        label: "Papierkorb leeren",
        beschreibung: "Endgültig gelöschte Inhalte entfernen.",
        kategorie: "administration",
    },
    {
        schluessel: "export.nutzen",
        label: "Export nutzen",
        beschreibung: "Daten der Instanz exportieren.",
        kategorie: "administration",
    },
    {
        schluessel: "projekte.verwalten",
        label: "Projekte verwalten",
        beschreibung: "Projekte anlegen und ihre Stammdaten pflegen.",
        kategorie: "projekte_aufgaben",
    },
    {
        schluessel: "aufgaben.bearbeiten",
        label: "Aufgaben bearbeiten",
        beschreibung: "Aufgaben anlegen und ändern.",
        kategorie: "projekte_aufgaben",
    },
    {
        schluessel: "gitlab.konfigurieren",
        label: "GitLab konfigurieren",
        beschreibung: "GitLab-Projektpfade und automatische Merge-Request-Zustände konfigurieren.",
        kategorie: "projekte_aufgaben",
    },
    {
        schluessel: "gitlab.code",
        label: "GitLab-Code schreiben",
        beschreibung: "Von Metarr vorbereitete und ausdrücklich freigegebene Code-Arbeitspakete in GitLab veröffentlichen.",
        kategorie: "projekte_aufgaben",
    },
    {
        schluessel: "gitlab.review",
        label: "GitLab Review",
        beschreibung: "Reviews auf verbundenen Merge Requests abgeben. Wirkt außerhalb des Systems.",
        kategorie: "projekte_aufgaben",
    },
    {
        schluessel: "gitlab.merge",
        label: "GitLab Merge",
        beschreibung: "Merge Requests zusammenführen. Bewusst getrennt, weil sich das nicht zurücknehmen lässt.",
        kategorie: "projekte_aufgaben",
    },
    {
        schluessel: "gitlab.gate_uebersteuern",
        label: "Qualitäts-Gate übersteuern",
        beschreibung: "Ein blockierendes Qualitäts-Gate ausnahmsweise und begründet für genau eine Revision freigeben.",
        kategorie: "projekte_aufgaben",
    },
    {
        schluessel: "seiten.bearbeiten",
        label: "Seiten bearbeiten",
        beschreibung: "Wissensseiten anlegen und ändern.",
        kategorie: "wissen_content",
    },
    {
        schluessel: "content.bearbeiten",
        label: "Content bearbeiten",
        beschreibung: "Content-Stücke anlegen und ändern.",
        kategorie: "wissen_content",
    },
    {
        schluessel: "content.veroeffentlichen",
        label: "Content veröffentlichen",
        beschreibung: "Content freigeben und veröffentlichen.",
        kategorie: "wissen_content",
    },
    {
        schluessel: "kommentare.schreiben",
        label: "Kommentare schreiben",
        beschreibung: "Kommentare und Randnotizen verfassen.",
        kategorie: "zusammenarbeit",
    },
    {
        schluessel: "mcp.nutzen",
        label: "MCP nutzen",
        beschreibung: "Zugang über MCP. Bewusst nicht in den Standardrechten — Daten laufen durch ein fremdes Modell.",
        kategorie: "zusammenarbeit",
    },
];
/**
 * Enthält die Liste `system.admin`, sind alle zwanzig Rechte wirksam.
 * Unbekannte Schlüssel werden vorher still verworfen.
 */
export function erweitereRechte(liste) {
    const bereinigt = bereinigeRechte(liste);
    if (bereinigt.has("system.admin")) {
        return new Set(RECHTE);
    }
    return bereinigt;
}
/** Unbekannte Schlüssel still verwerfen, bekannte behalten. */
export function bereinigeRechte(liste) {
    const ergebnis = new Set();
    for (const eintrag of liste) {
        if (RECHTE_SET.has(eintrag)) {
            ergebnis.add(eintrag);
        }
    }
    return ergebnis;
}
/**
 * Welche dieser Rechte darf der Handelnde **nicht** vergeben?
 *
 * Die Invariante lautet: **Niemand vergibt, was er nicht selbst hat.** Ohne sie
 * ist jedes Recht, das Gruppen verwalten darf, in Wahrheit `system.admin` — man
 * legt eine Gruppe mit allem an und tritt ihr bei. `bereinigeRechte` half dabei
 * nicht: Sie verwirft nur **unbekannte** Schlüssel und prüfte nie, ob der
 * Handelnde den bekannten überhaupt besitzt.
 *
 * `system.admin` schließt über `erweitereRechte` alles ein, wer es hat, darf
 * also alles vergeben. Unbekannte Schlüssel gelten als nicht vergebbar, statt
 * still zu verschwinden — wer etwas Unbekanntes vergeben will, soll es merken.
 *
 * Rückgabe: die Teilmenge, die fehlt. Leer heißt erlaubt.
 */
export function nichtVergebbar(eigene, gewuenscht) {
    if (eigene.has("system.admin"))
        return [];
    const fehlend = [];
    for (const recht of gewuenscht) {
        if (!eigene.has(recht))
            fehlend.push(recht);
    }
    return [...new Set(fehlend)].sort();
}
/** Vereinigung mehrerer Rechtemengen, danach Erweiterung um `system.admin`. */
export function vereinigeRechte(...listen) {
    const roh = [];
    for (const liste of listen) {
        roh.push(...liste);
    }
    return erweitereRechte(roh);
}
/* -------------------------------------------------------------------------
 * Systemgruppen
 * ---------------------------------------------------------------------- */
/**
 * Die zwei Gruppen, die immer existieren und nicht löschbar sind.
 *
 * `Admin` trägt `system.admin` und damit alle Rechte.
 * `Benutzer` trägt die Standardarbeitsrechte — bewusst **ohne** `mcp.nutzen`
 * sowie **ohne** `gitlab.konfigurieren`, `gitlab.merge` und
 * `gitlab.gate_uebersteuern`.
 */
export const SYSTEMGRUPPEN = {
    admin: {
        name: "Admin",
        beschreibung: "Vollzugriff auf die Instanz.",
        rechte: ["system.admin"],
    },
    benutzer: {
        name: "Benutzer",
        beschreibung: "Standardarbeitsrechte für angemeldete Personen.",
        rechte: [
            "projekte.verwalten",
            "aufgaben.bearbeiten",
            "seiten.bearbeiten",
            "content.bearbeiten",
            "content.veroeffentlichen",
            "kommentare.schreiben",
        ],
    },
};
/* -------------------------------------------------------------------------
 * Die Sicht
 * ---------------------------------------------------------------------- */
const SICHT = Symbol("sicht");
/**
 * Die einzige Stelle, an der eine Sicht entsteht — aus einer angemeldeten
 * Person. Der Server ruft das nach der Prüfung des Authentik-Tokens auf.
 */
export function sichtFuerPerson(personId, rollen, rechte = new Set()) {
    return {
        [SICHT]: true,
        personId,
        rollen,
        rechte,
        handelnd: "person",
        laufId: null,
        nurLesend: false,
    };
}
/**
 * Metarrs Sicht — abgeleitet, niemals eigenständig.
 *
 * Sie kann nur **enger** sein als die der Person: Die Rollenkarte wird
 * unverändert übernommen oder auf einzelne Projekte eingeschränkt. Die
 * Rechtemenge wird nie vergrößert. Es gibt keinen Parameter, der Rechte
 * hinzufügt.
 *
 * `nurLesend` ist die Voreinstellung: Während der Modellerzeugung liest Metarr
 * nur. Schreiben geschieht danach, nach ausdrücklicher Bestätigung durch einen
 * Menschen, und dann über die Sicht **der Person** — nicht über diese hier.
 */
export function fuerMetarr(person, laufId, nurProjekte) {
    const rollen = nurProjekte
        ? new Map([...person.rollen].filter(([projektId]) => nurProjekte.includes(projektId)))
        : person.rollen;
    return {
        [SICHT]: true,
        personId: person.personId,
        rollen,
        // Rechte unverändert übernehmen — verengen der Rollenkarte genügt;
        // hinzufügen ist strukturell unmöglich.
        rechte: person.rechte,
        handelnd: "metarr",
        laufId,
        nurLesend: true,
    };
}
/** Dasselbe für einen verbundenen MCP-Client. */
export function fuerMcp(person, laufId, nurLesend, nurProjekte) {
    const abgeleitet = fuerMetarr(person, laufId, nurProjekte);
    return { ...abgeleitet, handelnd: "mcp", nurLesend };
}
/* -------------------------------------------------------------------------
 * Prüfungen
 * ---------------------------------------------------------------------- */
export function hatRecht(sicht, schluessel) {
    return sicht.rechte.has(schluessel);
}
export function darfLesen(sicht, projektId) {
    return sicht.rollen.has(projektId);
}
export function darfSchreiben(sicht, projektId) {
    if (sicht.nurLesend)
        return false;
    const rolle = sicht.rollen.get(projektId);
    return rolle !== undefined && mindestens(rolle, "mitglied");
}
export function darfVerwalten(sicht, projektId) {
    if (sicht.nurLesend)
        return false;
    const rolle = sicht.rollen.get(projektId);
    return rolle !== undefined && mindestens(rolle, "verwalter");
}
/** Die Projekte, die diese Sicht überhaupt kennt. */
export function sichtbareProjekte(sicht) {
    return [...sicht.rollen.keys()];
}
/**
 * Warum etwas nicht ging.
 *
 * Wichtig für Metarr: Eine verweigerte Sicht wird zur **erklärbaren Antwort**,
 * nicht zu einer leeren. „Dieses Projekt liegt außerhalb deiner Sicht" ist eine
 * Auskunft; ein leeres Ergebnis wäre eine falsche.
 */
export class Abgewiesen extends Error {
    grund;
    art;
    erwartet;
    tatsaechlich;
    markenIds;
    constructor(grund, art, nachricht, zusatz) {
        super(nachricht ?? standardtext(grund, art));
        this.grund = grund;
        this.art = art;
        this.name = "Abgewiesen";
        if (zusatz?.erwartet !== undefined)
            this.erwartet = zusatz.erwartet;
        if (zusatz?.tatsaechlich !== undefined) {
            this.tatsaechlich = zusatz.tatsaechlich;
        }
        if (zusatz?.markenIds !== undefined)
            this.markenIds = zusatz.markenIds;
    }
}
function standardtext(grund, art) {
    switch (grund) {
        case "nicht_sichtbar":
            return `Diese ${art} liegt außerhalb deiner Sicht.`;
        case "nicht_vorhanden":
            return `Diese ${art} gibt es nicht.`;
        case "nur_lesend":
            return `Dieser Lauf darf lesen, aber nicht schreiben — ${art} bleibt unverändert.`;
        case "rolle_zu_niedrig":
            return `Deine Rolle reicht für diese Änderung an ${art} nicht aus.`;
        case "nicht_erlaubt":
            return `Dir fehlt das Recht für diese Aktion an ${art}.`;
        case "versionskonflikt":
            return `Die ${art} wurde inzwischen geändert. Lies sie neu.`;
        case "markenverlust":
            return `Beim Schreiben würden Textstellen an ${art} verschwinden.`;
        case "ungueltiger_inhalt":
            return `Der Inhalt von ${art} ist ungültig.`;
        case "wiederholungskonflikt":
            return `Dieselbe Geräte-Revision wurde mit anderem Inhalt erneut gesendet.`;
        case "gitlab_nicht_eingerichtet":
            return `Das GitLab-Seitenarchiv ist nicht eingerichtet.`;
        case "gitlab_abweichung":
            return `Das GitLab-Seitenarchiv weicht vom erwarteten Stand ab.`;
        case "qualitaets_gate":
            return `Das Qualitäts-Gate hält diese ${art} auf.`;
    }
}
//# sourceMappingURL=rechte.js.map